import React, { useState, useEffect, useReducer, useMemo } from 'react';
import './ItRequestAdminSingleView.css';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { SEC_ApiCall } from '../func.js';
import { RequestToolbar } from '../common/toolbar/RequestToolbar.js';
import { Return } from '../common/toolbar/Return.js';
import { AssignUser } from '../common/toolbar/AssignUser.js';
import { AddNote } from '../common/toolbar/AddNote.js';
import { RequestStatusBanner } from '../common/RequestStatusComponents.js';
import { NoteInputComponent } from '../common/toolbar/NoteInputComponent.js';
import { ExpandableLinkButton } from '../common/ExpandableLinkButton.js';
import { RequestFilesList } from '../common/RequestFilesList.js';
import { MarkCompleteButton } from '../common/toolbar/MarkCompleteButton.js';
import { MarkIncompleteButton } from '../common/toolbar/MarkIncompleteButton.js';

export default function ItUserSrv() {
  const [modalState, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'SET_MODAL':
        return {
          ...state,
          active: true,
          apiState: action.payload.apiState,
          message: action.payload.message || '',
        };
      default:
        return state;
    }
  }, { active: false, apiState: '', message: '' });

  const [dataState, setDataState] = useState([]);
  const [requestIdState, setRequestIdState] = useState(null);
  const [itAssignableUsersState, setItAssignableUsersState] = useState([]);
  const [isLoadedState, setIsLoadedState] = useState(false);
  const [apiErrorState, setApiErrorState] = useState(false);
  const [apiErrorDataState, setApiErrorDataState] = useState('');
  const [requestStatusState, setRequestStatusState] = useState(false);

  const statusValue = useMemo(() => {
    if (dataState?.status && typeof dataState.status === 'object') {
      return dataState.status.name || 'Open';
    }
    if (dataState?.status) {
      return dataState.status;
    }
    return requestStatusState ? 'Complete' : 'Open';
  }, [dataState, requestStatusState]);

  const statusColour = useMemo(() => {
    if (dataState?.status && typeof dataState.status === 'object' && dataState.status.colour) {
      return dataState.status.colour;
    }
    return '#f9b233';
  }, [dataState]);

  const statusTextColour = useMemo(() => {
    if (dataState?.status && typeof dataState.status === 'object' && dataState.status.textColour) {
      return dataState.status.textColour;
    }
    return '#ffffff';
  }, [dataState]);

  async function apiCall(id) {
    var url = 'it-requests/user/single?requestId=' + id;
    var params = {};
    try {
      const response = await SEC_ApiCall(url, params, setApiErrorState);
      const jsonObj = JSON.parse(response);

      if (jsonObj.data.files && typeof jsonObj.data.files === 'string') {
        try {
          jsonObj.data.files = JSON.parse(jsonObj.data.files);
        } catch (e) {
          console.error('Error parsing files array:', e);
          jsonObj.data.files = [];
        }
      }

      if (jsonObj.data.links && typeof jsonObj.data.links === 'string') {
        try {
          jsonObj.data.links = JSON.parse(jsonObj.data.links);
        } catch (e) {
          console.error('Error parsing links array:', e);
          jsonObj.data.links = [];
        }
      }

      setDataState(jsonObj['data']);
      setRequestStatusState(jsonObj['data']["completed"] !== 0);
      setIsLoadedState(true);
    } catch (e) {
      console.log(e);
      setIsLoadedState(true);
      setApiErrorState(true);
      setApiErrorDataState(e);
    }
  };

  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const id = params.get('id');
    setRequestIdState(id);
    if (id) {
      apiCall(id);
    } else {
      setIsLoadedState(true);
      setApiErrorState(true);
    }
  }, []);

  if (!isLoadedState) {
    return (
      <div className='main_div'>
        <LoadingComp />
      </div>
    )
  } else if (apiErrorState) {
    return (
      <ApiError />
    )
  } else {
    return (
      <div className='internal_main_div'>
        <div className="request-container">
          <RequestToolbar
            leftButtons={<Return showTooltip={false} onClick={() => console.log("Return to table")} />}
            rightButtons={[
              requestStatusState ? (
                <MarkIncompleteButton
                  key="mark-incomplete"
                  optionsComponent={
                    <NoteInputComponent
                      requestId={dataState.requestId}
                      url="it-requests/single-it-request/incomplete"
                      updateApiCallFunction={apiCall}
                      placeholder="Enter your reason here..."
                      showPrivateToggle
                    />
                  }
                />
              ) : (
                <MarkCompleteButton
                  key="mark-complete"
                  optionsComponent={
                    <NoteInputComponent
                      requestId={dataState.requestId}
                      url="it-requests/single-it-request/complete"
                      updateApiCallFunction={apiCall}
                      placeholder="Enter your completion here..."
                    />
                  }
                />
              ),
              <AddNote
                key="add-note"
                optionsComponent={
                  <NoteInputComponent
                    requestId={dataState.requestId}
                    url="it-requests/single-it-request/notes"
                    updateApiCallFunction={apiCall}
                    placeholder="Enter your note here..."
                  />
                }
              />,
            ].filter(Boolean)}
          />

          <h2>IT Support Request Details - {dataState.requestId}</h2>
          <RequestStatusBanner
            label={`Status: ${statusValue}`}
            backgroundColor={statusColour}
            textColor={statusTextColour}
          />
          <div className='request-container-internal'>
            <div className="request-info">
              <p><strong>Title:</strong> {dataState.requestTitle}</p>
              <p><strong>Description:</strong> {dataState.requestEvents.primaryEvent.eventData}</p>
              <p><strong>Location:</strong> {dataState.requestLocation}</p>
              <p><strong>Submitted By:</strong> {dataState.submittingUser}</p>
              <p><strong>Submitted On:</strong> {new Date(dataState.submissionTimestamp).toLocaleString("en-GB")}</p>
              {dataState.assignedUser !== "None" && (
                <p><strong>Assigned To:</strong> {dataState.assignedUser}</p>
              )}
              <p><strong>Required Date:</strong> {new Date(dataState.requiredDate).toLocaleDateString("en-GB")}</p>

              {Array.isArray(dataState.links) && dataState.links.length > 0 && (
                <>
                  <p><strong>Links:</strong></p>
                  <p style={{ fontSize: '13px', color: '#666', marginTop: '-8px', marginBottom: '10px' }}>
                    ⚠️ School Engine does not check or verify the contents of links. Only click links if you trust the destination.
                  </p>
                  <div style={{ marginBottom: '15px' }}>
                    {dataState.links.map((link, i) => (
                      <ExpandableLinkButton link={link} key={i} />
                    ))}
                  </div>
                </>
              )}

              <RequestFilesList
                files={dataState.files}
                folder="itRequests"
                itemId={requestIdState}
                title="Attached Files"
                dispatch={dispatch}
              />
            </div>

            <div className="request-events">
              <h3>Notes</h3>
              {dataState.requestEvents.additionalEvents.length > 0 && (
                <div className="additional-events">
                  {dataState.requestEvents.additionalEvents.map((event, index) => (
                    <div
                      key={index}
                      className={`event${event?.privateNote ? ' event-private' : ''}`}
                    >
                      <div className='event-user-timestamp'>
                        <p>{event.user}</p>
                        <p>{new Date(event.timestamp).toLocaleString("en-GB")}</p>
                      </div>
                      <p>{event.eventData}</p>
                      {event?.subNote && <p className="event-sub-note">Note: {event.subNote}</p>}

                      {Array.isArray(event.files) && event.files.length > 0 && (
                        <div className="event-attached-files">
                          <RequestFilesList
                            files={event.files}
                            folder="itRequests"
                            itemId={requestIdState}
                            title="Files"
                            groupKey={index}
                            dispatch={dispatch}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {modalState.active && (
          <div className={`toast ${modalState.apiState}`}>
            <span>{modalState.message}</span>
          </div>
        )}
      </div>
    )
  }
};
